import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { format, parseISO } from 'date-fns';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { fmtMinutes } from './usageApi';

const TABS = [
  { key: 'active_users', label: 'Active users', kpi: 'active_users' },
  { key: 'views', label: 'Page views', kpi: 'views' },
  { key: 'sessions', label: 'Sessions', kpi: 'sessions' },
  { key: 'active_minutes', label: 'Active time', kpi: 'total_minutes', time: true },
];

const hourLabel = (h) => `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;

export const DeltaBadge = ({ cur, prev, testId }) => {
  if (!prev) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (!pct) return null;
  const up = pct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${up ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
      <Icon className="h-3 w-3" />{up ? '+' : ''}{pct}%
    </span>
  );
};

export const UsageVisitsPanel = ({ data }) => {
  const [metric, setMetric] = useState('active_users');
  const tick = (b) => (data.granularity === 'hour' ? hourLabel(b) : format(parseISO(b), 'd MMM'));
  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-[#1c1c1c]" data-testid="usage-visits-panel">
      <div className="grid grid-cols-2 md:grid-cols-4">
        {TABS.map((t) => {
          const cur = data.kpis[t.kpi] ?? 0;
          const active = metric === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setMetric(t.key)}
              data-testid={`usage-kpi-${t.key}`}
              className={`border-b border-r border-white/10 px-4 py-3.5 text-left transition-colors last:border-r-0 ${active ? 'bg-[#141414]' : 'bg-[#232323] hover:bg-[#1f1f1f]'}`}
            >
              <div className={`text-[11px] font-medium uppercase tracking-wider ${active ? 'text-neutral-300' : 'text-neutral-500'}`}>{t.label}</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xl font-semibold text-white tabular-nums" data-testid={`usage-kpi-${t.key}-value`}>
                  {t.time ? fmtMinutes(cur) : cur.toLocaleString()}
                </span>
                <DeltaBadge cur={cur} prev={data.previous[t.kpi]} testId={`usage-kpi-${t.key}-delta`} />
              </div>
            </button>
          );
        })}
      </div>
      <div className="px-4 pb-3 pt-4">
        <div className="mb-2 flex justify-end gap-4 text-[11px] text-neutral-400">
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 rounded bg-[#3b82f6]" />this period</span>
          <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 rounded bg-neutral-500" />last period</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 800, height: 256 }}>
            <LineChart data={data.trend} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#2a2a2a" vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={tick} tick={{ fontSize: 11, fill: '#8a8a8a' }} tickLine={false} axisLine={false} minTickGap={28} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#8a8a8a' }} tickLine={false} axisLine={false} />
              <Tooltip
                labelFormatter={tick}
                contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12, color: '#eee' }}
              />
              <Line type="monotone" dataKey={`prev_${metric}`} name="Last period" stroke="#6b6b6b" strokeWidth={1.75} dot={false} />
              <Line type="monotone" dataKey={metric} name="This period" stroke="#3b82f6" strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
